import { Injectable } from '@angular/core';
import Konva from 'konva';
import { Subject } from 'rxjs';
import { ProductGroup } from '../../planogram/models/product-group';
import { Point } from '../models/point';

type DragOutsideItem = {
	key: ProductGroup;
	canvasProduct: Konva.Group;
};

type PreviewEntry = {
	element: HTMLDivElement;
	rect: { left: number; top: number; width: number; height: number };
	dropPoint: Point;
};

@Injectable()
export class DragOutsideService {
	private previewElements: Map<ProductGroup, PreviewEntry> = new Map();
	public readonly drop$ = new Subject<HTMLElement>();
	public readonly redrawCanvas$ = new Subject<void>();

	update(options: {
		items: Array<DragOutsideItem>;
		stage: Konva.Stage;
		container: HTMLElement;
		overCanvasTargets?: Array<HTMLElement>;
	}): void {
		const { items, stage, container, overCanvasTargets = [] } = options;
		if (!stage || !container) return;

		const stageContent = stage.getContent();
		if (!stageContent) return;

		const stageRect = stageContent.getBoundingClientRect();
		const containerRect = container.getBoundingClientRect();
		const activeOutsideKeys = new Set<ProductGroup>();

		const rects = overCanvasTargets
			.filter((el) => !!el)
			.map((el) => el.getBoundingClientRect())
			.filter((rect) => rect.width > 0 && rect.height > 0);

		items.forEach(({ key, canvasProduct }) => {
			if (!canvasProduct || !canvasProduct.getLayer()) return;
			const rect = canvasProduct.getClientRect({ relativeTo: stage });

			const left = stageRect.left + rect.x;
			const top = stageRect.top + rect.y;
			const right = left + rect.width;
			const bottom = top + rect.height;

			const isOutside = left < containerRect.left
				|| top < containerRect.top
				|| right > containerRect.right
				|| bottom > containerRect.bottom;
			const isOccluded = rects.some((occluder) =>
				left < occluder.right
				&& right > occluder.left
				&& top < occluder.bottom
				&& bottom > occluder.top
			);
			if (!isOutside && !isOccluded) return;

			activeOutsideKeys.add(key);
			const anchor = canvasProduct.getAbsoluteTransform().point({ x: 0, y: canvasProduct.height() });
			const dropPoint: Point = { x: stageRect.left + anchor.x, y: stageRect.top + anchor.y };

			let preview = this.previewElements.get(key);
			if (!preview) {
				const element = this.createPreviewElement(canvasProduct);
				preview = { element, rect: { left, top, width: rect.width, height: rect.height }, dropPoint };
				this.previewElements.set(key, preview);
			}

			preview.element.style.left = `${left}px`;
			preview.element.style.top = `${top}px`;
			preview.element.style.width = `${rect.width}px`;
			preview.element.style.height = `${rect.height}px`;
			preview.rect = { left, top, width: rect.width, height: rect.height };
			preview.dropPoint = dropPoint;
		});

		Array.from(this.previewElements.entries()).forEach(([key, entry]) => {
			if (!activeOutsideKeys.has(key)) {
				entry.element.remove();
				this.previewElements.delete(key);
			}
		});
	}

	clear(): void {
		if (!this.previewElements.size) return;
		this.previewElements.forEach((entry) => entry.element.remove());
		this.previewElements.clear();
	}

	getDropPoint(pg: ProductGroup, stage: Konva.Stage): Point {
		const dropPoint: Point = this.previewElements.get(pg)?.dropPoint;
		const stageRect: DOMRect = stage?.getContent()?.getBoundingClientRect();

		if (dropPoint && stageRect) {
			return {
				x: dropPoint.x - stageRect.left,
				y: dropPoint.y - stageRect.top
			}
		} else {
			return null;
		}
	}

	emitDropIfTargetDifferent(activeProducts: ProductGroup[], canvases: HTMLElement[], sourceCanvas: HTMLElement): HTMLElement | null {
		if (!this.previewElements.size) return null;
		if (!activeProducts.length) return null;

		for (const product of activeProducts) {
			const preview = this.previewElements.get(product);
			if (!preview) continue;
			const targetCanvas = this.findTargetCanvas(preview.dropPoint, canvases);
			if (!targetCanvas || targetCanvas === sourceCanvas) continue;

			this.drop$.next(targetCanvas);
			this.clear();
			return targetCanvas;
		}

		return null;
	}

	private findTargetCanvas(point: Point, canvases: HTMLElement[]): HTMLElement | null {
		if (!canvases.length) return null;
		const elementAtPoint = document.elementFromPoint(point.x, point.y) as HTMLElement | null;
		if (elementAtPoint) {
			const direct = canvases.find((canvas) => canvas.contains(elementAtPoint));
			if (direct) return direct;
		}

		return canvases.find((canvas) => {
			const rect = canvas.getBoundingClientRect();
			return point.x >= rect.left
				&& point.x <= rect.right
				&& point.y >= rect.top
				&& point.y <= rect.bottom;
		}) || null;
	}

	private createPreviewElement(canvasProduct: Konva.Group): HTMLDivElement {
		const element = document.createElement('div');
		element.className = 'drag-outside-preview-body';
		element.style.position = 'fixed';
		element.style.zIndex = '2147483647';
		element.style.pointerEvents = 'none';
		element.style.display = 'block';
		element.style.transformOrigin = 'center';
		element.style.boxSizing = 'content-box';
		element.style.overflow = 'visible';

		const snapshotCanvas = canvasProduct.toCanvas({ pixelRatio: 1 });
		snapshotCanvas.style.width = '100%';
		snapshotCanvas.style.height = '100%';
		snapshotCanvas.style.display = 'block';

		element.appendChild(snapshotCanvas);
		document.body.appendChild(element);
		return element;
	}
}
